export default class LabelMaker {
    constructor(params) {
        this.params = params;
        this.legendLabels = [];
        this.xlabel = '';
        this.xLegendLabel = '';
        this.valueFormatX = '';
        this.ylabel = '';
        this.y2label = '';
        this.labels = [];
        this.ids = params.has('objId') ? params.get('objId').split(',') : [];
        this.linking = params.get('linking') === 'concatenate' ? 'concatenate' : 'overlap';
        this.hasY2 = params.has('y2');
        if (params.has('legendLabels'))
            this.legendLabels = params.get('legendLabels').split(',');
        this.legendDataSerieIndexes = getLegendDataIndexes(this.linking, this.ids, this.hasY2);
        this.legendTitles = {
            y: this.hasY2 ? params.get('y') : 'Legend',
            y2: params.get('y2')
        };
    }
    set objects(objects) {
        const sortedObjects = objects.sort((obj1, obj2) => new Date(obj1.startedAtTime).getTime() - new Date(obj2.startedAtTime).getTime());
        const specList = Array.from(new Set(sortedObjects.map(o => o.specLabel))).join(' / ');
        this.chartTitle = this.hasY2
            ? `${specList} - ${this.params.get('y')} and ${this.params.get('y2')}`
            : `${specList} - ${this.params.get('y')}`;
        this.metadata = sortedObjects.map((object, idx) => new Metadata(this.linking, object, idx, this.params));
        this.object = sortedObjects.find(object => isColNameValid(object.tableFormat, this.params.get('x'))
            && isColNameValid(object.tableFormat, this.params.get('y'))
            && (!this.hasY2 || isColNameValid(object.tableFormat, this.params.get('y2'))));
        if (this.object) {
            this.xlabel = getLabel(this.object.tableFormat, this.params.get('x'));
            this.xLegendLabel = getColInfoParam(this.object.tableFormat, this.params.get('x'), 'label');
            this.valueFormatX = getColInfoParam(this.object.tableFormat, this.params.get('x'), 'valueFormat');
            this.ylabel = getLabel(this.object.tableFormat, this.params.get('y'));
            if (this.hasY2)
                this.y2label = getLabel(this.object.tableFormat, this.params.get('y2'));
            if (this.linking === 'concatenate') {
                this.labels = this.hasY2
                    ? [this.xlabel].concat(this.metadata[0].labels.y).concat(this.metadata[0].labels.y2)
                    : [this.xlabel].concat(this.metadata[0].labels.y);
            }
            else {
                this.labels = this.hasY2
                    ? [this.xlabel].concat(this.metadata.flatMap(dobj => [dobj.labels.y, dobj.labels.y2]))
                    : [this.xlabel].concat(this.metadata.map(dobj => dobj.labels.y));
            }
        }
    }
    get title() {
        return !window.frameElement && this.chartTitle || ' ';
    }
    get series() {
        if (!this.hasY2 || !this.metadata || this.metadata.some(dobj => dobj.labels.y2 === undefined))
            return {};
        if (this.object) {
            const mwtf = this.object;
            const yColInfo = (ycol, key) => {
                return getColInfoParam(mwtf.tableFormat, this.params.get(ycol), key);
            };
            const sameValueType = ['label', 'unit'].every(key => yColInfo('y', key) === yColInfo('y2', key));
            if (sameValueType)
                return {};
        }
        return this.metadata.reduce((acc, dobj) => {
            acc[dobj.labels.y] = { axis: 'y1' };
            acc[dobj.labels.y2] = { axis: 'y2' };
            return acc;
        }, {});
    }
    legendData(data) {
        return {
            y: this.legendDataSerieIndexes.y.map(i => data.series[i]),
            y2: this.legendDataSerieIndexes.y2.map(i => data.series[i])
        };
    }
    get uniqueLabels() {
        return this.labels.reduce((acc, lbl, idx) => {
            if (acc.includes(lbl))
                acc.push(`${idx} ${lbl}`);
            else
                acc.push(lbl);
            return acc;
        }, []);
    }
}
const getLegendDataIndexes = (linking, ids, hasY2) => {
    if (linking === 'concatenate') {
        if (hasY2) {
            return {
                y: [0],
                y2: [1]
            };
        }
        else {
            return {
                y: [0],
                y2: []
            };
        }
    }
    else {
        const length = hasY2 ? ids.length * 2 : ids.length;
        const indexes = Array.from({ length }, (_, i) => i);
        if (hasY2) {
            return {
                y: indexes.filter(idx => idx % 2 === 0),
                y2: indexes.filter(idx => idx % 2 !== 0)
            };
        }
        else {
            return {
                y: indexes,
                y2: []
            };
        }
    }
};
class Metadata {
    constructor(linking, object, idx, params) {
        this.object = object;
        const hasY2 = params.has('y2');
        const postfix = hasY2 && linking !== 'concatenate' ? ' '.repeat(idx + 2) : '';
        const postfixY2 = linking === 'concatenate' ? '' : ' ';
        const paramsYLabel = getLegendLabelFromParams(params, 'legendLabels', object.id);
        const yLabel = getFinalLabel(linking, paramsYLabel, object, params.get('y'), postfix);
        let y2Label = undefined;
        if (hasY2) {
            const paramsY2Label = getLegendLabelFromParams(params, 'legendLabelsY2', object.id);
            y2Label = getFinalLabel(linking, paramsY2Label, object, params.get('y2'), postfixY2);
        }
        this.labels = {
            y: yLabel,
            y2: y2Label
        };
    }
}
const getFinalLabel = (linking, requestedLabel, object, colName, postfix = '') => {
    if (requestedLabel)
        return requestedLabel + postfix;
    if (linking === 'concatenate') {
        return getLabel(object.tableFormat, colName) + postfix;
    }
    const filename = object.filename;
    return filename.slice(0, filename.lastIndexOf('.')) + postfix;
};
const getLegendLabelFromParams = (params, name, objId) => {
    const labels = params.has(name) ? params.get(name).split(',') : [];
    const ids = params.has('objId') ? params.get('objId').split(',') : [];
    if (labels.length === 0 || ids.length === 0)
        return '';
    const idx = ids.findIndex(id => objId.includes(id));
    return (idx !== -1 && labels[idx]) ? labels[idx] : '';
};
const isColNameValid = (tableFormat, colName) => {
    return tableFormat.getColumnIndex(colName) >= 0;
};
const getLabel = (tableFormat, colName) => {
    const unit = getColInfoParam(tableFormat, colName, 'unit');
    const label = getColInfoParam(tableFormat, colName, 'label');
    return unit === '?' ? label : `${label} [${unit}]`;
};
function getColInfoParam(tableFormat, colName, param) {
    return tableFormat.columns[tableFormat.getColumnIndex(colName)][param];
}
;
